import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  ChevronLeft, ChevronRight, RefreshCw, Search, Settings,
  Check, X, ArrowRight, ArrowLeft, ArrowUpDown
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

export default function DataManagement() {
  // --- STATE ---
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState('');

  // Data State
  const [tableData, setTableData] = useState({
    data: [],
    columns: [],
    total: 0,
    page: 1,
    total_pages: 1,
    limit: 30
  });

  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Table Control State
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [globalSearch, setGlobalSearch] = useState("");
  const [columnFilters, setColumnFilters] = useState({});
  const [visibleColumns, setVisibleColumns] = useState({});
  const [columnWidths, setColumnWidths] = useState({});

  // Debounce ref for filters
  const filterTimeoutRef = useRef(null);

  // --- 1. FETCH TABLES ---
  const fetchTables = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/tables`);
      setTables(response.data);
    } catch (error) {
      console.error("Error fetching tables:", error);
      toast.error("Nu s-au putut incarca tabelele.");
    }
  }, []);

  useEffect(() => {
    fetchTables();
  }, [fetchTables]);

  // --- 2. FETCH DATA ---
  const fetchData = useCallback(async (tableName, page = 1, search = "", sort = null, filters = {}) => {
    if (!tableName) return;

    setLoading(true);
    try {
      // Build Query Params manually to handle dynamic filters
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 30);
      if (search) params.append('search', search);
      if (sort && sort.key) {
        params.append('sort_by', sort.key);
        params.append('order', sort.direction);
      }

      // Append Column Filters
      Object.entries(filters).forEach(([key, val]) => {
        if (val) params.append(key, val);
      });

      const url = `${API}/data/${tableName}?${params.toString()}`;
      const response = await axios.get(url);

      const serverColumns = response.data.columns || [];

      // Initialize visibility/widths if new fields appear or on first load
      setTableData(prev => {
        // If columns changed (or first load), init visibility
        const currentCols = serverColumns.length > 0 ? serverColumns : Object.keys(response.data.data[0] || {});

        // Only reset if completely empty or table changed (handled by useEffect dependency)
        // But here we just return data. State updates for visual config happen in useEffect when table changes.
        return {
          ...response.data,
          rows: response.data.data, // Map backend 'data' to 'rows' for clarity specific here if needed, but keeping 'data' matches backend
          columns: currentCols
        };
      });

    } catch (error) {
      console.error(`Error fetching data for ${tableName}:`, error);
      toast.error(`Eroare la incarcarea datelor pentru ${tableName}`);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // --- 3. EVENT HANDLERS ---

  // Reset state when table changes
  useEffect(() => {
    if (selectedTable) {
      setSortConfig({ key: null, direction: 'asc' });
      setGlobalSearch("");
      setColumnFilters({});
      setTableData(prev => ({ ...prev, data: [], columns: [] })); // Clear view
      // Fetch initial
      fetchData(selectedTable, 1, "", null, {});
    }
  }, [selectedTable, fetchData]);

  // Auto-init visibility/widths when columns are loaded
  useEffect(() => {
    if (tableData.columns.length > 0) {
      setVisibleColumns(prev => {
        // Preserve existing settings if possible, else default true
        const newVis = { ...prev };
        let changed = false;
        tableData.columns.forEach(col => {
          if (newVis[col] === undefined) {
            newVis[col] = true;
            changed = true;
          }
        });
        return changed ? newVis : prev;
      });

      setColumnWidths(prev => {
        const newWidths = { ...prev };
        let changed = false;
        tableData.columns.forEach(col => {
          if (!newWidths[col]) {
            newWidths[col] = 150; // Default width
            changed = true;
          }
        });
        return changed ? newWidths : prev;
      });
    }
  }, [tableData.columns]);


  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= tableData.total_pages) {
      fetchData(selectedTable, newPage, globalSearch, sortConfig, columnFilters);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    const newSort = { key, direction };
    setSortConfig(newSort);
    fetchData(selectedTable, 1, globalSearch, newSort, columnFilters);
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...columnFilters, [key]: value };
    setColumnFilters(newFilters);

    // Debounce fetch
    if (filterTimeoutRef.current) clearTimeout(filterTimeoutRef.current);

    filterTimeoutRef.current = setTimeout(() => {
      fetchData(selectedTable, 1, globalSearch, sortConfig, newFilters);
    }, 500);
  };

  const handleGlobalSearch = (val) => {
    setGlobalSearch(val);
    if (filterTimeoutRef.current) clearTimeout(filterTimeoutRef.current);
    filterTimeoutRef.current = setTimeout(() => {
      fetchData(selectedTable, 1, val, sortConfig, columnFilters);
    }, 500);
  };

  const toggleColumn = (key, value) => {
    setVisibleColumns(prev => ({ ...prev, [key]: value }));
  };

  const handleResizeMouseDown = (e, key) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.pageX;
    const startWidth = columnWidths[key] || 150;

    const onMouseMove = (moveEvent) => {
      const currentX = moveEvent.pageX;
      const diff = currentX - startX;
      setColumnWidths(prev => ({
        ...prev,
        [key]: Math.max(50, startWidth + diff)
      }));
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  // --- RENDER HELPERS ---
  const ResizableHeader = ({ label, colKey }) => (
    <TableHead
      className="text-gray-200 font-bold text-xs uppercase p-0 relative border-r border-white/10 bg-[#27272a] select-none group align-top h-auto"
      style={{ width: columnWidths[colKey], maxWidth: columnWidths[colKey], minWidth: columnWidths[colKey] }}
    >
      <div className="flex flex-col h-full justify-between">
        {/* Top: Label & Sort */}
        <div
          className="flex items-center justify-between p-2 cursor-pointer hover:bg-white/5 flex-1"
          onClick={() => handleSort(colKey)}
        >
          <span className="mr-1 whitespace-normal break-words leading-tight" title={label}>
            {label}
          </span>
          <div className="flex flex-col">
            {sortConfig.key === colKey ? (
              <ArrowUpDown className={`h-3 w-3 ${sortConfig.direction === 'asc' ? 'text-cyan-400' : 'text-cyan-400 rotate-180'}`} />
            ) : (
              <ArrowUpDown className="h-3 w-3 text-gray-600 opacity-0 group-hover:opacity-100" />
            )}
          </div>
        </div>

        {/* Bottom: Filter Input */}
        <div className="px-1 pb-1" onClick={(e) => e.stopPropagation()}>
          <input
            className="w-full text-[10px] p-1 border border-white/20 rounded text-white bg-black/20 focus:outline-none focus:border-cyan-500 placeholder:text-gray-600"
            placeholder="Filtreaza..."
            value={columnFilters[colKey] || ''}
            onChange={(e) => handleFilterChange(colKey, e.target.value)}
          />
        </div>
      </div>

      {/* Resize Handle */}
      <div
        className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-cyan-500/50 z-10"
        onMouseDown={(e) => handleResizeMouseDown(e, colKey)}
      />
    </TableHead>
  );

  return (
    <div className="p-8">
      {/* HEADER SECTION */}
      <div className="mb-8 flex flex-col xl:flex-row xl:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Explorator Date
          </h1>
          <p className="text-gray-400">Vizualizeaza si gestioneaza datele importate</p>
        </div>

        {/* CONTROLS CLUSTER */}
        <div className="flex flex-wrap items-center gap-2 bg-[#18181b] p-2 rounded-lg border border-white/10">

          {/* Table Selector */}
          <span className="text-gray-400 text-sm ml-2">Tabela:</span>
          <Select value={selectedTable} onValueChange={setSelectedTable}>
            <SelectTrigger className="w-[200px] bg-[#27272a] border-white/20 text-white">
              <SelectValue placeholder="Selecteaza tabela" />
            </SelectTrigger>
            <SelectContent className="bg-[#27272a] border-white/20 text-white">
              {tables.map(t => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="h-6 w-px bg-white/10 mx-2" />

          {/* Global Search */}
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
            <input
              type="text"
              placeholder="Cauta global..."
              value={globalSearch}
              onChange={(e) => handleGlobalSearch(e.target.value)}
              className="pl-8 bg-[#27272a] text-white p-2 rounded border border-white/20 text-sm w-[200px] focus:outline-none focus:border-cyan-500 placeholder:text-gray-600"
            />
          </div>

          {/* Column Visibility Toggle */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="bg-[#27272a] border-white/20 text-white hover:bg-white/10" disabled={!selectedTable}>
                <Settings className="h-4 w-4 mr-2" />
                Coloane
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[600px] bg-zinc-950 border border-zinc-800 p-0 shadow-2xl overflow-hidden" align="end">
              <div className="grid grid-cols-2 max-h-[600px]">
                {/* Visible Columns */}
                <div className="border-r border-white/10 flex flex-col min-h-0">
                  <div className="p-3 border-b border-white/10 bg-green-500/10">
                    <h3 className="text-green-400 font-semibold flex items-center text-sm">
                      <Check className="w-4 h-4 mr-2" /> Coloane Afisate
                    </h3>
                  </div>
                  <ScrollArea className="flex-1 p-2 bg-zinc-950">
                    {tableData.columns.map(col => visibleColumns[col] && (
                      <div
                        key={col}
                        className="flex items-center justify-between p-2 rounded bg-white/5 border border-white/5 hover:border-green-500/50 cursor-pointer group mb-1"
                        onClick={() => toggleColumn(col, false)}
                      >
                        <span className="text-sm text-gray-200 truncate pr-2">{col}</span>
                        <ArrowRight className="w-4 h-4 text-gray-500 group-hover:text-green-400 shrink-0" />
                      </div>
                    ))}
                  </ScrollArea>
                </div>

                {/* Hidden Columns */}
                <div className="flex flex-col min-h-0">
                  <div className="p-3 border-b border-white/10 bg-red-500/10">
                    <h3 className="text-red-400 font-semibold flex items-center text-sm">
                      <X className="w-4 h-4 mr-2" /> Coloane Ascunse
                    </h3>
                  </div>
                  <ScrollArea className="flex-1 p-2 bg-zinc-950">
                    {tableData.columns.map(col => !visibleColumns[col] && (
                      <div
                        key={col}
                        className="flex items-center justify-between p-2 rounded bg-white/5 border border-white/5 hover:border-red-500/50 cursor-pointer group mb-1"
                        onClick={() => toggleColumn(col, true)}
                      >
                        <ArrowLeft className="w-4 h-4 text-gray-500 group-hover:text-red-400 shrink-0" />
                        <span className="text-sm text-gray-200 truncate pl-2">{col}</span>
                      </div>
                    ))}
                    {Object.values(visibleColumns).every(v => v) && (
                      <div className="text-center text-gray-600 italic py-10 text-sm">
                        Toate coloanele sunt afisate.
                      </div>
                    )}
                  </ScrollArea>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          <Button
            variant="ghost"
            size="icon"
            onClick={() => { setRefreshing(true); if (selectedTable) fetchData(selectedTable, tableData.page, globalSearch, sortConfig, columnFilters); else fetchTables(); }}
            className="text-cyan-400 hover:bg-cyan-400/10"
          >
            <RefreshCw className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* DATA AREA */}
      <div className="bg-[#18181b] rounded-xl border border-white/10 p-6 shadow-xl">
        {loading || refreshing ? (
          <div className="flex flex-col items-center justify-center h-[500px] text-gray-400 animate-pulse">
            <RefreshCw className="h-8 w-8 mb-4 animate-spin text-cyan-500" />
            Se incarca datele...
          </div>
        ) : (!selectedTable) ? (
          <div className="flex items-center justify-center h-[500px] text-gray-400 italic">
            Selecteaza o tabela din lista de mai sus pentru a incepe.
          </div>
        ) : (tableData.data.length === 0) ? (
          <div className="flex items-center justify-center h-[500px] text-gray-400">
            Nu exista date pentru filtrele selectate.
          </div>
        ) : (
          <div className="space-y-4">
            <ScrollArea className="h-[600px] rounded-md border border-white/10 bg-[#0f0f11]">
              <Table>
                <TableHeader className="sticky top-0 z-20 shadow-md">
                  <TableRow className="border-b border-white/10 bg-[#27272a] hover:bg-[#27272a]">
                    {(tableData.columns || []).map((col) =>
                      visibleColumns[col] && (
                        <ResizableHeader key={col} colKey={col} label={col} />
                      )
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tableData.data.map((row, idx) => (
                    <TableRow key={idx} className="border-white/5 hover:bg-white/5 transition-colors">
                      {(tableData.columns || []).map((col) =>
                        visibleColumns[col] && (
                          <TableCell
                            key={`${idx}-${col}`}
                            className="text-gray-300 font-mono text-xs border-r border-white/5 truncate"
                            style={{ maxWidth: columnWidths[col], minWidth: columnWidths[col], width: columnWidths[col] }}
                            title={row[col] !== null ? String(row[col]) : ''}
                          >
                            {row[col] !== null ? String(row[col]) : <span className="text-gray-700">-</span>}
                          </TableCell>
                        )
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>

            {/* Pagination */}
            <div className="flex items-center justify-between border-t border-white/10 pt-4">
              <div className="text-sm text-gray-400">
                Pagina <span className="text-white font-bold">{tableData.page}</span> din {tableData.total_pages} ({tableData.total} inregistrari)
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(tableData.page - 1)}
                  disabled={tableData.page <= 1}
                  className="bg-transparent border-white/20 text-white hover:bg-white/10"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Precedent
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(tableData.page + 1)}
                  disabled={tableData.page >= tableData.total_pages}
                  className="bg-transparent border-white/20 text-white hover:bg-white/10"
                >
                  Urmator
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}